import {
    type RoundedCalculations,
    calculateRoundedDayMetrics,
} from "@client/calculations";
import {Eyebrow, SelectChevron} from "@client/components/Chrome";
import {EYEBROW} from "@client/components/styles";
import type {Props as JournalProps} from "@client/scenes/Journal";
import * as utils from "@client/utils";
import {classNames, server} from "@reactivated";
import * as React from "react";

type Metric = keyof RoundedCalculations;

const FAT_SUBS: [Metric, string, string][] = [
    ["saturated_fat", "Saturated", "g"],
    ["monounsaturated_fat", "Monounsaturated", "g"],
    ["polyunsaturated_fat", "Polyunsaturated", "g"],
    ["trans_fat", "Trans", "g"],
];

const CARB_SUBS: [Metric, string, string][] = [
    ["fiber", "Fiber", "g"],
    ["sugars", "Total sugars", "g"],
    ["added_sugars", "Added sugars", "g"],
    ["net_carbs", "Net carbs", "g"],
];

const MICROS: [Metric, string, string][] = [
    ["sodium", "Sodium", "mg"],
    ["cholesterol", "Cholesterol", "mg"],
    ["potassium", "Potassium", "mg"],
    ["magnesium", "Magnesium", "mg"],
    ["vitamin_a_dv", "Vitamin A", "IU"],
    ["vitamin_c_dv", "Vitamin C", "mg"],
    ["calcium_dv", "Calcium", "mg"],
    ["iron_dv", "Iron", "mg"],
];

const GRID = "grid grid-cols-[minmax(0,1fr)_72px_72px_72px] items-baseline gap-x-1";
const MUTED = "text-[oklch(0.45_0.03_280)]";
const LEFT = "text-[oklch(0.45_0.19_278)]";
const HAIRLINE = "border-b border-[oklch(0.35_0.04_280_/_0.08)]";
const GLASS =
    "rounded-[14px] bg-white/55 shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.9)]";

const Dash = () => <span className="text-[oklch(0.68_0.02_280)]">—</span>;

// A thin space before the unit, so "225 g" reads as a number and its unit.
const Unit = ({children}: {children: string}) => (
    <span className="text-[11px] text-[oklch(0.55_0.02_280)]">{" " + children}</span>
);

type Columns = {
    total: RoundedCalculations;
    target: RoundedCalculations;
    remaining: RoundedCalculations;
};

const present = (columns: Columns, metric: Metric) =>
    columns.total[metric] != null || columns.target[metric] != null;

// The rows the old table always listed; the rest appear when they have data.
const ALWAYS: Metric[] = ["saturated_fat", "fiber", "sugars", "net_carbs"];

const MacroRow = ({
    label,
    metric,
    columns,
    lead = false,
}: {
    label: string;
    metric: Metric;
    columns: Columns;
    lead?: boolean;
}) => (
    <div className={classNames(GRID, lead ? "pt-3 pb-2" : "py-[7px]")}>
        <span
            className={classNames(
                "font-normal",
                lead ? "text-[15px]" : "text-[14.5px]",
            )}
        >
            {label}
        </span>
        <span className="text-right text-[15px] font-light">
            {columns.total[metric] ?? "0"}
            {!lead && <Unit>g</Unit>}
        </span>
        <span className={classNames("text-right text-[15px] font-light", MUTED)}>
            {columns.target[metric] ?? <Dash />}
        </span>
        <span className={classNames("text-right text-[15px] font-light", LEFT)}>
            {columns.remaining[metric] ?? <Dash />}
        </span>
    </div>
);

const SmallRow = ({
    label,
    metric,
    unit,
    columns,
    indent,
    last,
}: {
    label: string;
    metric: Metric;
    unit: string;
    columns: Columns;
    indent: boolean;
    last: boolean;
}) => (
    <div
        className={classNames(
            GRID,
            "text-[13px] font-light",
            indent
                ? "pl-3.5 text-[oklch(0.36_0.03_280)]"
                : "text-[oklch(0.32_0.03_280)]",
            last ? classNames("pt-1 pb-[11px]", HAIRLINE) : "py-1",
        )}
    >
        <span className="truncate">{label}</span>
        <span className="text-right">
            {columns.total[metric] ?? <Dash />}
            {columns.total[metric] != null && <Unit>{unit}</Unit>}
        </span>
        <span className="text-right">{columns.target[metric] ?? <Dash />}</span>
        <span className="text-right">{columns.remaining[metric] ?? <Dash />}</span>
    </div>
);

const Group = ({
    rows,
    columns,
    indent,
    top = false,
}: {
    rows: [Metric, string, string][];
    columns: Columns;
    indent: boolean;
    top?: boolean;
}) => {
    const shown = rows.filter(
        ([metric]) => ALWAYS.includes(metric) || present(columns, metric),
    );
    return (
        <div className={classNames(top && "pt-2.5")}>
            {shown.map(([metric, label, unit], index) => (
                <SmallRow
                    key={metric}
                    label={label}
                    metric={metric}
                    unit={unit}
                    columns={columns}
                    indent={indent}
                    last={index === shown.length - 1}
                />
            ))}
        </div>
    );
};

const Donut = ({
    summary,
}: {
    summary: ReturnType<typeof calculateRoundedDayMetrics>["total"]["summary"];
}) => {
    const protein = Number.isNaN(summary.protein.percentage)
        ? 0
        : summary.protein.percentage;
    const carbs = Number.isNaN(summary.carbs.percentage) ? 0 : summary.carbs.percentage;
    const fat = Number.isNaN(summary.fat.percentage) ? 0 : summary.fat.percentage;
    return (
        <div className="flex items-center gap-4 pt-[13px]">
            <div
                className="grid size-12 flex-none place-items-center rounded-full"
                style={{
                    background:
                        protein + carbs + fat === 0
                            ? "oklch(0.35 0.04 280 / 0.08)"
                            : `conic-gradient(oklch(0.51 0.21 278) 0 ${protein}%, oklch(0.74 0.11 278) ${protein}% ${protein + carbs}%, oklch(0.72 0.14 35) ${protein + carbs}%)`,
                }}
            >
                <div className="size-8 rounded-full bg-white" />
            </div>
            <div className="flex flex-1 justify-between">
                {(
                    [
                        ["Protein", protein],
                        ["Carbs", carbs],
                        ["Fat", fat],
                    ] as const
                ).map(([label, value]) => (
                    <span key={label} className="text-[13px]">
                        <span className="block text-[11.5px] text-[oklch(0.5_0.03_280)]">
                            {label}
                        </span>
                        <span className="text-[15px] font-medium">{value}%</span>
                    </span>
                ))}
            </div>
        </div>
    );
};

interface Props {
    useKilojoules: boolean;
    day: server.journal.nutrition.Day.output;
    target: server.journal.nutrition.Target.output | null;
    targets: server.journal.nutrition.Target.output[];
    pForm: JournalProps["pForm"];
    profile: server.core.rpc.Profile.output;
}

export const DayForm = (props: Props) => {
    const columns = calculateRoundedDayMetrics(
        props.day,
        props.target,
        props.useKilojoules,
    );
    const {pForm} = props;
    const included = pForm.fields.include_in_reports.value;

    return (
        <div className="flex flex-col gap-2.5 tracking-[-0.01em] text-[oklch(0.24_0.02_280)]">
            {props.day.comments.map((comment, index) => (
                <div key={index} className={classNames(GLASS, "px-4 py-3")}>
                    <div className="mb-1 text-[10px] font-medium tracking-[0.08em] text-[oklch(0.52_0.03_280)] uppercase">
                        {comment.manager.profile.name} commented{" "}
                        {utils.formatDistanceToNow(comment.created_at)} ago
                    </div>
                    <div className="text-[13.5px] font-light whitespace-pre-wrap">
                        {comment.comment}
                    </div>
                </div>
            ))}
            {props.profile.has_hidden_macros == false && (
                <div className="rounded-[14px] bg-white px-4 py-3.5">
                    <div
                        className={classNames(
                            GRID,
                            "border-b border-[oklch(0.25_0.02_280)] pb-[9px]",
                            EYEBROW,
                            MUTED,
                        )}
                    >
                        <span />
                        <span className="text-right">Total</span>
                        <span className="text-right">Target</span>
                        <span className="text-right">Left</span>
                    </div>
                    <MacroRow
                        label={props.useKilojoules ? "Kilojoules" : "Calories"}
                        metric="calories"
                        columns={columns}
                        lead
                    />
                    <MacroRow label="Protein" metric="protein" columns={columns} />
                    <MacroRow label="Fat" metric="fat" columns={columns} />
                    <Group rows={FAT_SUBS} columns={columns} indent />
                    <MacroRow label="Carbs" metric="carbs" columns={columns} />
                    <Group rows={CARB_SUBS} columns={columns} indent />
                    <Group rows={MICROS} columns={columns} indent={false} top />
                    <Donut summary={columns.total.summary} />
                </div>
            )}
            <div className="flex items-center gap-2.5">
                <button
                    type="button"
                    role="checkbox"
                    aria-checked={included}
                    aria-label="Include in reports"
                    onClick={() => pForm.fields.include_in_reports.onChange(!included)}
                    className={classNames(
                        "grid size-[22px] flex-none place-items-center rounded-[7px]",
                        included ? "bg-[oklch(0.51_0.21_278)]" : "bg-white/55",
                    )}
                >
                    {included && (
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="2.6"
                        >
                            <path d="M5 12.5l4.5 4.5L19 7" />
                        </svg>
                    )}
                </button>
                <span className="flex-1 text-[13px] font-medium">
                    Include in reports
                </span>
                {props.targets.length > 1 && (
                    <label className="relative flex h-9 items-center gap-2 rounded-full bg-white pr-3 pl-3.5 text-[13px] font-medium">
                        <select
                            aria-label="Macro targets"
                            value={pForm.fields.target.value ?? ""}
                            onChange={(event) =>
                                pForm.fields.target.onChange(event.target.value)
                            }
                            className="absolute inset-0 appearance-none opacity-0"
                        >
                            {props.targets.map((target) => (
                                <option key={target.pk} value={target.pk.toString()}>
                                    {target.name}
                                </option>
                            ))}
                        </select>
                        {props.target?.name ?? "Target"}
                        <SelectChevron className="-mr-1" />
                    </label>
                )}
            </div>
            <div>
                <Eyebrow className="mb-1.5">Notes</Eyebrow>
                <div className={classNames(GLASS, "px-4 py-3")}>
                    <textarea
                        aria-label="Notes"
                        rows={4}
                        placeholder="Add a note about today…"
                        value={pForm.fields.notes.value ?? ""}
                        onChange={(event) =>
                            pForm.fields.notes.onChange(event.target.value)
                        }
                        className="block min-h-[88px] w-full resize-none bg-transparent text-[13.5px] font-light outline-none placeholder:text-[oklch(0.6_0.02_280)]"
                    />
                </div>
            </div>
        </div>
    );
};
